import type {
  CampaignCreationCapabilities,
  SmartCreationObjectiveRecommendation,
} from "@adpilot/shared";
import { IconExclamationCircle } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverTitle,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { useDelayedPending } from "@/hooks/use-delayed-pending";
import { formatCurrency, type RecommendationField } from "./objectives-model";

export function ConstraintField({
  field,
  label,
  value,
  min,
  max,
  step,
  prefix,
  suffix,
  capabilities,
  minimumBudget,
  recommendation,
  recommendationCurrent,
  recommendationPending,
  onChange,
  onApply,
}: {
  field: RecommendationField;
  label: string;
  value: number | "";
  min: number;
  max?: number;
  step: number;
  prefix?: string;
  suffix?: string;
  capabilities: CampaignCreationCapabilities;
  minimumBudget: number;
  recommendation: SmartCreationObjectiveRecommendation | null;
  recommendationCurrent: boolean;
  recommendationPending: boolean;
  onChange: (value: number) => void;
  onApply: () => void;
}) {
  const { t } = useTranslation("smartCreation");
  const loadingVisible = useDelayedPending(recommendationPending, 300, 300);
  const inputId =
    field === "targetAcos" ? "smart-creation-target-acos" : "smart-creation-daily-budget";
  const summary = recommendation
    ? field === "targetAcos"
      ? {
          value: `${(recommendation.targetAcos.value * 100).toFixed(2)}%`,
          range: `${(recommendation.targetAcos.range.min * 100).toFixed(1)}%–${(recommendation.targetAcos.range.max * 100).toFixed(1)}%`,
        }
      : {
          value: formatCurrency(
            recommendation.totalDailyBudget.value,
            recommendation.totalDailyBudget.currencyCode,
            recommendation.totalDailyBudget.decimals,
          ),
          range: `${formatCurrency(
            recommendation.totalDailyBudget.range.min,
            recommendation.totalDailyBudget.currencyCode,
            recommendation.totalDailyBudget.decimals,
          )}–${formatCurrency(
            recommendation.totalDailyBudget.range.max,
            recommendation.totalDailyBudget.currencyCode,
            recommendation.totalDailyBudget.decimals,
          )}`,
        }
    : null;
  const budgetIsMinimumOnly =
    field === "totalDailyBudget" && recommendation?.totalDailyBudget.source === "capability_floor";
  const targetIsPolicyStart =
    field === "targetAcos" &&
    recommendation !== null &&
    recommendation.targetAcos.source !== "selected_product_history";
  const summaryLabel = summary
    ? budgetIsMinimumOnly
      ? t("objectives.noHistoricalBudgetRecommendation")
      : targetIsPolicyStart
        ? t("objectives.startingValueSummary", summary)
        : t("objectives.recommendationSummary", summary)
    : null;

  return (
    <Field className="rounded-md border border-line bg-surface p-3.5">
      <div className="flex items-center gap-1.5">
        <FieldLabel htmlFor={inputId}>{label}</FieldLabel>
        <RecommendationCalculation
          field={field}
          label={label}
          recommendation={recommendation}
          capabilities={capabilities}
          minimumBudget={minimumBudget}
        />
      </div>
      <InputGroup className="hide-number-spin">
        {prefix ? (
          <InputGroupAddon>
            <InputGroupText>{prefix}</InputGroupText>
          </InputGroupAddon>
        ) : null}
        <InputGroupInput
          id={inputId}
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          className="font-mono tabular-nums"
          onChange={(event) => onChange(event.target.valueAsNumber)}
        />
        {suffix ? (
          <InputGroupAddon align="inline-end">
            <InputGroupText>{suffix}</InputGroupText>
          </InputGroupAddon>
        ) : null}
      </InputGroup>
      <div
        data-slot="objective-recommendation"
        aria-busy={recommendationPending}
        className="grid h-8 w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-2"
      >
        {loadingVisible ? (
          <div role="status" className="col-span-2">
            <Skeleton className="h-8 w-full" />
            <span className="sr-only">{t("objectives.recommendationLoading")}</span>
          </div>
        ) : !recommendationPending && summary && recommendationCurrent ? (
          <>
            <span className="min-w-0 truncate text-xs text-muted-foreground">
              {budgetIsMinimumOnly ? (
                summaryLabel
              ) : (
                <>
                  {summaryLabel?.split(summary.value)[0]}
                  <strong className="font-semibold text-ink">{summary.value}</strong>
                  {summaryLabel?.split(summary.value).slice(1).join(summary.value)}
                </>
              )}
            </span>
            {!budgetIsMinimumOnly ? (
              <Button
                type="button"
                size="xs"
                variant="outline"
                disabled={!recommendationCurrent}
                onClick={onApply}
              >
                {t(
                  targetIsPolicyStart
                    ? "objectives.actions.useStartingValue"
                    : "objectives.actions.useRecommendation",
                )}
              </Button>
            ) : null}
          </>
        ) : (
          <span aria-hidden="true" className="col-span-2 h-8" />
        )}
      </div>
    </Field>
  );
}

interface RecommendationCalculationProps {
  field: RecommendationField;
  label: string;
  recommendation: SmartCreationObjectiveRecommendation | null;
  capabilities: CampaignCreationCapabilities;
  minimumBudget: number;
}

function RecommendationCalculation({
  field,
  label,
  recommendation,
  capabilities,
  minimumBudget,
}: RecommendationCalculationProps) {
  const { t } = useTranslation("smartCreation");
  const kind = field === "targetAcos" ? "acos" : "budget";
  const evidence = recommendation?.evidence;
  const historical = recommendation?.[field].source === "selected_product_history";
  const campaignCount = minimumBudget / capabilities.dailyBudget.min;
  const maximumBudget = Math.min(1_000_000, capabilities.dailyBudget.max * campaignCount);
  const baseline =
    historical && evidence
      ? kind === "acos"
        ? `${((evidence.metrics.observedAcos ?? 0) * 100).toFixed(2)}%`
        : formatCurrency(
            evidence.metrics.averageDailySpend,
            capabilities.currencyCode,
            capabilities.dailyBudget.decimals,
          )
      : null;

  return (
    <Popover>
      <PopoverTrigger
        render={
          <button
            type="button"
            aria-label={t("objectives.calculation.trigger", { label })}
            className="grid size-6 shrink-0 place-items-center rounded-sm text-muted-foreground outline-none hover:bg-subtle hover:text-ink focus-visible:ring-2 focus-visible:ring-ring"
          />
        }
      >
        <IconExclamationCircle className="size-3.5" stroke={1.75} aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={8} className="w-[380px] max-w-[calc(100vw-32px)]">
        <div className="flex flex-col gap-3 text-xs leading-5">
          <PopoverTitle className="text-sm leading-5">
            {t("objectives.calculation.title", { label })}
          </PopoverTitle>
          <PopoverDescription className="text-xs leading-5">
            {baseline && evidence
              ? t(`objectives.calculation.${kind}.historical`, {
                  days: evidence.actualWindowDays,
                  baseline,
                })
              : t(`objectives.calculation.${kind}.${recommendation ? "fallback" : "basis"}`)}
          </PopoverDescription>
          {kind === "budget" && recommendation?.totalDailyBudget.source === "capability_floor" ? (
            <p>
              {t("objectives.calculation.budget.minimumOnly", {
                minimum: formatCurrency(
                  minimumBudget,
                  capabilities.currencyCode,
                  capabilities.dailyBudget.decimals,
                ),
              })}
            </p>
          ) : (
            <>
              <p>{t(`objectives.calculation.${kind}.formula`)}</p>
              <p className="text-muted-foreground">{t(`objectives.calculation.${kind}.factors`)}</p>
              <p>{t(`objectives.calculation.${kind}.range`)}</p>
              <p className="text-muted-foreground">
                {kind === "acos"
                  ? t("objectives.calculation.acos.limits")
                  : t("objectives.calculation.budget.limits", {
                      minimum: formatCurrency(
                        minimumBudget,
                        capabilities.currencyCode,
                        capabilities.dailyBudget.decimals,
                      ),
                      maximum: formatCurrency(
                        maximumBudget,
                        capabilities.currencyCode,
                        capabilities.dailyBudget.decimals,
                      ),
                      decimals: capabilities.dailyBudget.decimals,
                    })}
              </p>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
